import React from 'react';
import { Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Users, Shield, Heart, Compass, ArrowRight } from 'lucide-react';
import QuickAnswer from '../components/QuickAnswer';

const Community = () => {
  return (
    <div className="bg-gradient-to-b from-[#e8f4fd] to-[#f0f8ff] min-h-screen py-20">
      <div className="max-w-4xl mx-auto px-6">
        <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6 text-center">
          The Levanta Community
        </h1>
        <p className="text-xl text-gray-600 text-center mb-10">
          A community built on structure, discipline, and long-term commitment.
        </p>

        <QuickAnswer label="What the Community is">
          The Levanta Community is the community of long-term thinkers inside
          Levanta who hold each other accountable to a shared standard: growth
          through structure, not inspiration. It replaces social-feed chaos
          with focused support for real development across the four Worlds.
        </QuickAnswer>

        <div className="space-y-8">
          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Why the Community exists
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Growth in isolation is fragile. Motivation fades fastest when
              nobody else sees you try. Every sustainable long-term change
              happens inside a social structure.
            </p>
            <p className="text-gray-700 leading-relaxed">
              The Community gives Levanta members other people who value the same
              things — discipline, structure, and patient progress — so that
              when your own motivation dips, the environment keeps you moving.
            </p>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What the Community is not
            </h2>
            <ul className="space-y-2 text-gray-700">
              <li>• Not a social feed. There is no endless scrolling.</li>
              <li>• Not a hype group. Inspiration is not the product.</li>
              <li>• Not a network marketing tier. Nobody recruits you.</li>
              <li>• Not a chat room. Every conversation has purpose.</li>
            </ul>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              What the Community offers
            </h2>
            <div className="grid md:grid-cols-2 gap-5">
              <div className="bg-blue-50 rounded-2xl p-5">
                <Shield className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Accountability</h3>
                <p className="text-sm text-gray-700">
                  Shared commitments that hold you to the standard you set for
                  yourself.
                </p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-5">
                <Compass className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Shared direction</h3>
                <p className="text-sm text-gray-700">
                  Everyone is building toward the same four Worlds of growth.
                </p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-5">
                <Users className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Peer learning</h3>
                <p className="text-sm text-gray-700">
                  Real experience from people a few steps ahead of where you
                  are.
                </p>
              </div>
              <div className="bg-blue-50 rounded-2xl p-5">
                <Heart className="w-6 h-6 text-blue-600 mb-2" />
                <h3 className="font-bold text-gray-900 mb-1">Long-term belonging</h3>
                <p className="text-sm text-gray-700">
                  A place to grow that you never outgrow.
                </p>
              </div>
            </div>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              Who joins the Community
            </h2>
            <p className="text-gray-700 leading-relaxed mb-3">
              Members of the Community are the same people described on the{' '}
              <Link to="/who-its-for" className="text-blue-600 hover:underline">
                Who It&apos;s For
              </Link>{' '}
              page — individuals who want structure over inspiration,
              discipline over motivation, and long-term growth over quick fixes.
            </p>
            <p className="text-gray-700 leading-relaxed">
              You don&apos;t need prior experience in self-development. You just
              need to be willing to show up consistently.
            </p>
          </Card>

          <Card className="bg-white rounded-3xl p-8 shadow-sm">
            <h2 className="text-2xl font-bold text-gray-900 mb-4">
              How the Community fits into the system
            </h2>
            <p className="text-gray-700 leading-relaxed">
              The Community is the third pillar of Levanta, alongside Discipline
              and Structure. Members use the community to share progress, get
              feedback on practice, and stay committed through the bad weeks
              that break every other self-help effort. It is the social layer
              that turns the{' '}
              <Link to="/daily-practice" className="text-blue-600 hover:underline">
                daily practice loop
              </Link>{' '}
              into a lifestyle.
            </p>
          </Card>
        </div>

        <div className="text-center mt-12">
          <Link to="/download-app">
            <Button
              data-testid="community-cta"
              className="bg-blue-500 hover:bg-blue-600 text-white px-10 py-5 text-lg rounded-xl shadow-md"
            >
              Join the Community
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
};

export default Community;
